import { api, TAGS } from "@/store/api"
import { cellsSocket } from "@/store/apis/cellsAPI/cells.socket"
import { mergeCellSnapshot } from "@/store/apis/mobsAPI/cellCache"
import {
  applyMobCellEvent,
  applyMobCellEvents,
  isEventOfCell
} from "@/store/apis/mobsAPI/cellEvents"
import { requestCellHeal } from "@/store/apis/mobsAPI/cellHeal"
import { cellKeyOf, cellReads } from "@/store/apis/mobsAPI/cellReads"
import {
  IGetCellMobsReq,
  IGetMobCardReq,
  IMobAttackRequest,
  IMobCard,
  IMobCellResponse,
  IMobEngagement,
  IMobKill,
  IMobLeaveResponse,
  IMobRedistributeRequest,
  IMobRedistributeResponse,
  IMobTypeListResponse,
  IMobWinResponse,
  IOpenEngagementResponse
} from "@/store/apis/mobsAPI/mobs.types"
import { IMobCellEvent, SOCKET_EVENTS } from "@/store/apis/socketRegistry"

export const mobsAPI = api
  .enhanceEndpoints({
    addTagTypes: [
      TAGS.MOBS_CELL,
      TAGS.MOB_ENGAGEMENT,
      TAGS.HERO,
      TAGS.INVENTORY,
      TAGS.WALLET
    ]
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getCellMobs: build.query<IMobCellResponse, IGetCellMobsReq>({
        queryFn: async (arg, _api, _extraOptions, baseQuery) => {
          const read = cellReads.begin(cellKeyOf(arg))
          try {
            const result = await baseQuery({
              url: `/mobs/cells/${arg.x}/${arg.y}`,
              method: "GET",
              params: { mapId: arg.mapId }
            })
            if (result.error) {
              return { error: result.error }
            }
            return {
              data: applyMobCellEvents(
                result.data as IMobCellResponse,
                read.events()
              )
            }
          } finally {
            read.close()
          }
        },
        merge: (current, incoming) => mergeCellSnapshot(current, incoming),
        providesTags: [TAGS.MOBS_CELL],
        async onCacheEntryAdded(
          arg,
          { updateCachedData, cacheEntryRemoved, dispatch }
        ) {
          const cellKey = cellKeyOf(arg)
          const applyEvent = (event: IMobCellEvent): void => {
            if (!isEventOfCell(event, arg)) {
              return
            }
            cellReads.record(cellKey, event)
            updateCachedData((draft) => applyMobCellEvent(draft, event))
          }
          const unbindChanged = cellsSocket.bindCellEvent(
            SOCKET_EVENTS.mobChanged,
            applyEvent
          )
          const unbindRemoved = cellsSocket.bindCellEvent(
            SOCKET_EVENTS.mobRemoved,
            applyEvent
          )
          const stopConfirmations = cellsSocket.onConfirmation(
            ({ cell, delayMs }) => {
              if (cell !== null && cellKeyOf(cell) === cellKey) {
                dispatch(requestCellHeal(arg, delayMs))
              }
            }
          )
          try {
            await cacheEntryRemoved
          } finally {
            unbindChanged()
            unbindRemoved()
            stopConfirmations()
          }
        }
      }),
      getMobCard: build.query<IMobCard, IGetMobCardReq>({
        query: ({ typeCode, level }) => ({
          url: `/mobs/types/${typeCode}/levels/${level}`,
          method: "GET"
        })
      }),
      getOpenEngagement: build.query<IOpenEngagementResponse, void>({
        query: () => ({ url: "/mobs/engagements/open", method: "GET" }),
        providesTags: [TAGS.MOB_ENGAGEMENT]
      }),
      getMobKill: build.query<IMobKill, string>({
        query: (id) => ({ url: `/mobs/kills/${id}`, method: "GET" })
      }),
      devAttackMob: build.mutation<IMobEngagement, IMobAttackRequest>({
        query: (data) => ({ url: "/mobs/dev/attack", method: "POST", data }),
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled
            dispatch(
              mobsAPI.util.upsertQueryData("getOpenEngagement", undefined, {
                engagement: data
              })
            )
          } catch {
            return
          }
        }
      }),
      devWinEngagement: build.mutation<IMobWinResponse, string>({
        query: (id) => ({
          url: `/mobs/dev/engagements/${id}/win`,
          method: "POST"
        }),
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled
            dispatch(
              mobsAPI.util.upsertQueryData(
                "getMobKill",
                data.kill.id,
                data.kill
              )
            )
            dispatch(
              mobsAPI.util.updateQueryData(
                "getOpenEngagement",
                undefined,
                () => ({ engagement: null })
              )
            )
          } catch {
            return
          }
        },
        invalidatesTags: [TAGS.HERO, TAGS.INVENTORY, TAGS.WALLET]
      }),
      devLeaveEngagement: build.mutation<IMobLeaveResponse, string>({
        query: (id) => ({
          url: `/mobs/dev/engagements/${id}/leave`,
          method: "POST"
        }),
        onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled
            dispatch(
              mobsAPI.util.updateQueryData(
                "getOpenEngagement",
                undefined,
                () => ({ engagement: null })
              )
            )
          } catch {
            return
          }
        }
      }),
      devRedistributeMobs: build.mutation<
        IMobRedistributeResponse,
        IMobRedistributeRequest
      >({
        query: (data) => ({
          url: "/mobs/dev/redistribute",
          method: "POST",
          data
        }),
        invalidatesTags: [TAGS.MOBS_CELL]
      }),
      devGetMobTypes: build.query<IMobTypeListResponse, void>({
        query: () => ({ url: "/mobs/dev/types", method: "GET" })
      })
    })
  })

export const {
  useGetCellMobsQuery,
  useGetMobCardQuery,
  useGetOpenEngagementQuery,
  useGetMobKillQuery,
  useDevAttackMobMutation,
  useDevWinEngagementMutation,
  useDevLeaveEngagementMutation,
  useDevRedistributeMobsMutation,
  useDevGetMobTypesQuery
} = mobsAPI
